import {
	Box,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	LinearProgress,
	Radio,
	RadioGroup,
	Tooltip,
	Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import React, { FC, useEffect, useMemo } from "react";
import SaveIcon from "@mui/icons-material/Save";
import ImageIcon from "@mui/icons-material/Image";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RouterIcon from "@mui/icons-material/Router";

import { useAppDispatch } from "../../../redux";
import {
	useGetSocietyImageLimitsQuery,
	useUpdateSocietyImageLimitsMutation
} from "../../../redux/reducers/settings.reducer";
import NumberInput from "../../../components/NumberInput";
import { showFeedbackNotification } from "../../../redux/reducers/feedbackNotification.reducer";
import { ISocietyImageLimitDeviceDetails, SOCIETY_IMAGE_LIMIT_PERIOD_TYPE } from "../../../types";
import { Controller, useForm } from "react-hook-form";

interface IImageLimitsTabProps {
	customerId: number;
}

interface IImageLimitsInputs {
	active_period_type: SOCIETY_IMAGE_LIMIT_PERIOD_TYPE;
	weekly_image_upload_limit: string;
	monthly_image_upload_limit: string;
}

function formatNumber(value: number | null | undefined): string {
	if (value === null || value === undefined) return "--";
	return value.toLocaleString("en-IN");
}

function formatDateTime(value: string | null | undefined): string {
	if (!value) return "--";
	return moment(value).format("DD-MM-YYYY, hh:mm A");
}

function getUsageColor(percentage: number, isExceeded: boolean): "success" | "warning" | "error" {
	if (isExceeded || percentage >= 100) return "error";
	if (percentage >= 75) return "warning";
	return "success";
}

const ImageLimitsTab: FC<IImageLimitsTabProps> = (props) => {
	const { customerId } = props;

	const dispatch = useAppDispatch();

	// APIS
	// GET SOCIETY IMAGE LIMITS
	const {
		data: imageLimitsResponse,
		isLoading: imageLimitsLoading,
		isFetching: imageLimitsFetching,
		refetch: refetchImageLimits
	} = useGetSocietyImageLimitsQuery(customerId, { skip: customerId < 0 });

	// UPDATE SOCIETY IMAGE LIMITS
	const [updateSocietyImageLimits, { isLoading: updateImageLimitsLoading, reset: resetUpdateImageLimitsState }] =
		useUpdateSocietyImageLimitsMutation();

	const {
		reset,
		watch,
		control,
		register,
		handleSubmit,
		formState: { errors, isDirty: inputChanged, isValid }
	} = useForm<IImageLimitsInputs>({
		defaultValues: {
			active_period_type: "monthly",
			weekly_image_upload_limit: "",
			monthly_image_upload_limit: ""
		}
	});

	const activePeriod = watch("active_period_type");

	const activeLimit = useMemo<number | null>(() => {
		if (!imageLimitsResponse) return null;
		return imageLimitsResponse.active_limit_per_device;
	}, [imageLimitsResponse]);

	function handleSaveButtonClick(formData: IImageLimitsInputs) {
		if (customerId < 0) return;

		const payload: {
			weekly_image_upload_limit: number | null;
			monthly_image_upload_limit: number | null;
			active_period_type: SOCIETY_IMAGE_LIMIT_PERIOD_TYPE;
		} = {
			active_period_type: formData.active_period_type,
			weekly_image_upload_limit: null,
			monthly_image_upload_limit: null
		};

		if (formData.active_period_type === "weekly") {
			payload.weekly_image_upload_limit = formData.weekly_image_upload_limit
				? Number(formData.weekly_image_upload_limit)
				: null;
		} else if (formData.active_period_type === "monthly") {
			payload.monthly_image_upload_limit = formData.monthly_image_upload_limit
				? Number(formData.monthly_image_upload_limit)
				: null;
		}

		updateSocietyImageLimits({
			society_id: customerId,
			data: payload
		})
			.unwrap()
			.then(() => {
				dispatch(
					showFeedbackNotification({
						message: "Image upload limits updated successfully",
						severity: "success"
					})
				);

				resetUpdateImageLimitsState();
				refetchImageLimits();
			})
			.catch((error) => {
				dispatch(
					showFeedbackNotification({
						message: "data" in error && error.data ? String(error.data) : "Failed to update image upload limits",
						severity: "error"
					})
				);

				resetUpdateImageLimitsState();
			});
	}

	useEffect(() => {
		if (imageLimitsResponse) {
			reset({
				active_period_type: imageLimitsResponse.active_period_type ?? "monthly",
				weekly_image_upload_limit:
					imageLimitsResponse.weekly_image_upload_limit !== null &&
					imageLimitsResponse.weekly_image_upload_limit !== undefined
						? String(imageLimitsResponse.weekly_image_upload_limit)
						: "",
				monthly_image_upload_limit:
					imageLimitsResponse.monthly_image_upload_limit !== null &&
					imageLimitsResponse.monthly_image_upload_limit !== undefined
						? String(imageLimitsResponse.monthly_image_upload_limit)
						: ""
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [imageLimitsResponse]);

	const devicesRowsData: ISocietyImageLimitDeviceDetails[] = imageLimitsResponse?.devices ?? [];
	const isDataLoading = imageLimitsLoading || imageLimitsFetching;

	return (
		<Box className="image-limits-tab-wrapper">
			<Box className="image-limits-summary-wrapper">
				<Box className={`image-limits-summary-card ${imageLimitsResponse?.any_device_exceeded ? "error" : "success"}`}>
					<Box className="icon-wrapper">
						{imageLimitsResponse?.any_device_exceeded ? <WarningAmberIcon /> : <CheckCircleOutlineIcon />}
					</Box>
					<Box>
						<Typography variant="caption" color="inherit">
							Overall Status
						</Typography>
						<Typography variant="subtitle2" color="inherit" fontWeight={600}>
							{imageLimitsResponse?.any_device_exceeded ? "Quota exceeded on a device" : "Within quota"}
						</Typography>
					</Box>
				</Box>

				<Box className="image-limits-summary-card neutral">
					<Box className="icon-wrapper">
						<ImageIcon />
					</Box>
					<Box>
						<Typography variant="caption" color="inherit">
							Active Limit / Device
						</Typography>
						<Typography variant="subtitle2" color="inherit" fontWeight={600}>
							{activeLimit !== null ? formatNumber(activeLimit) : "Not configured"}
							{imageLimitsResponse?.active_period_type ? (
								<Typography component="span" variant="caption" sx={{ marginLeft: "0.25rem", opacity: 0.8 }}>
									/ {imageLimitsResponse.active_period_type}
								</Typography>
							) : null}
						</Typography>
					</Box>
				</Box>

				<Box className="image-limits-summary-card neutral">
					<Box className="icon-wrapper">
						<AccessTimeIcon />
					</Box>
					<Box>
						<Typography variant="caption" color="inherit">
							Current Period
						</Typography>
						<Typography variant="subtitle2" color="inherit" fontWeight={600}>
							{imageLimitsResponse?.current_period_key ?? "--"}
						</Typography>
						<Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
							{imageLimitsResponse?.period_start_ist
								? `${moment(imageLimitsResponse.period_start_ist).format("DD MMM")} - ${moment(
										imageLimitsResponse.period_end_ist
								  ).format("DD MMM YYYY")}`
								: "--"}
						</Typography>
					</Box>
				</Box>
			</Box>

			<Box className="image-limits-section-wrapper">
				<Box className="reporting-title-wrapper">
					<Typography variant="h6" color="var(--color-primary-main)">
						Image Upload Limits
					</Typography>
				</Box>

				<Typography variant="body2" color="#00000080">
					Choose the active period and set one limit. The other period limit is automatically set to null when saved.
				</Typography>

				<Grid
					container
					spacing={2}
					sx={{ marginTop: "0.5rem" }}
					component="form"
					noValidate
					onSubmit={handleSubmit(handleSaveButtonClick)}
				>
					<Grid item xs={12}>
						<InputLabel classes={{ root: "customer-details-form-input-label-root" }} sx={{ marginBottom: "0.25rem" }}>
							Active Period
						</InputLabel>

						<Controller
							control={control}
							name="active_period_type"
							render={({ field: { value, onChange } }) => (
								<FormControl>
									<RadioGroup row value={value} onChange={onChange} sx={{ gap: "3rem" }}>
										<FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
										<FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
									</RadioGroup>
								</FormControl>
							)}
						/>
					</Grid>

					{activePeriod === "weekly" ? (
						<Grid item xs={12} md={6}>
							<InputLabel htmlFor="weekly-limit-input" classes={{ root: "customer-details-form-input-label-root" }}>
								Weekly Image Upload Limit
							</InputLabel>

							<NumberInput
								fullWidth
								positiveOnly
								disableSigned
								disableDecimal
								size="small"
								variant="outlined"
								id="weekly-limit-input"
								placeholder={`Enter weekly limit per device`}
								error={!!errors.weekly_image_upload_limit}
								helperText={errors.weekly_image_upload_limit?.message?.toString() || ""}
								InputProps={{
									endAdornment: <InputAdornment position="end">images</InputAdornment>
								}}
								{...register("weekly_image_upload_limit", { required: "Weekly limit is required" })}
							/>

							<Typography variant="caption" color="#00000080">
								Weekly limit will be cleared when you save.
							</Typography>
						</Grid>
					) : (
						<Grid item xs={12} md={6}>
							<InputLabel htmlFor="monthly-limit-input" classes={{ root: "customer-details-form-input-label-root" }}>
								Monthly Image Upload Limit
							</InputLabel>

							<NumberInput
								fullWidth
								positiveOnly
								disableSigned
								disableDecimal
								size="small"
								variant="outlined"
								id="monthly-limit-input"
								placeholder={`Enter monthly limit per device`}
								error={!!errors.monthly_image_upload_limit}
								helperText={errors.monthly_image_upload_limit?.message?.toString() || ""}
								InputProps={{
									endAdornment: <InputAdornment position="end">images</InputAdornment>
								}}
								{...register("monthly_image_upload_limit", { required: "Monthly limit is required" })}
							/>

							<Typography variant="caption" color="#00000080">
								Monthly limit will be cleared when you save.
							</Typography>
						</Grid>
					)}

					<Grid item xs={12}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
							<LoadingButton
								disableElevation
								variant="contained"
								color="success"
								startIcon={<SaveIcon />}
								disabled={!inputChanged || !isValid}
								loading={updateImageLimitsLoading}
								loadingPosition="start"
								type="submit"
							>
								Save Changes
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<Box className="image-limits-section-wrapper">
				<Box className="reporting-title-wrapper" sx={{ maxWidth: "unset" }}>
					<Typography variant="h6" color="var(--color-primary-main)">
						Device Usage
					</Typography>

					<Typography variant="caption" color="text.secondary">
						Last reported:{" "}
						<Box component="span" sx={{ fontWeight: 500 }}>
							{formatDateTime(imageLimitsResponse?.latest_reported_at)}
						</Box>
					</Typography>
				</Box>

				{isDataLoading ? (
					<Box className="image-limits-device-state-wrapper">
						<Typography variant="body2" color="text.secondary">
							Loading device usage...
						</Typography>
					</Box>
				) : devicesRowsData.length <= 0 ? (
					<Box className="image-limits-device-state-wrapper">
						<Typography variant="body2" color="text.secondary">
							No device usage data available.
						</Typography>
					</Box>
				) : (
					<Box className="image-limits-device-cards-wrapper">
						{devicesRowsData.map((device) => {
							const usagePercentage = Number(device.usage_percentage) || 0;
							const usageColor = getUsageColor(usagePercentage, device.is_quota_exceeded);
							const isOnline = String(device.device_status).toLowerCase() === "online";

							return (
								<Box key={device.device_id} className="image-limits-device-card">
									<Box className="image-limits-device-header">
										<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
											<RouterIcon fontSize="small" sx={{ color: "var(--color-primary-main)" }} />
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="body2" fontWeight={600} noWrap title={device.device_name}>
													{device.device_name}
												</Typography>
												<Typography variant="caption" color="text.secondary" noWrap title={device.device_uuid}>
													{device.device_uuid}
												</Typography>
											</Box>
										</Box>

										<Chip
											size="small"
											label={device.device_status || "--"}
											color={isOnline ? "success" : "default"}
											variant={isOnline ? "filled" : "outlined"}
										/>
									</Box>

									<Box className="image-limits-device-counts">
										<Box>
											<Typography variant="caption" color="text.secondary">
												Used
											</Typography>
											<Typography variant="body2" fontWeight={600}>
												{formatNumber(device.used)}
											</Typography>
										</Box>
										<Box>
											<Typography variant="caption" color="text.secondary">
												Remaining
											</Typography>
											<Typography variant="body2" fontWeight={600}>
												{formatNumber(device.remaining)}
											</Typography>
										</Box>
									</Box>

									<Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
										<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
											<Typography variant="caption" fontWeight={500}>
												Usage: {usagePercentage.toFixed(2)}%
											</Typography>
											{device.is_quota_exceeded ? (
												<Typography variant="caption" color="var(--color-error-main)" fontWeight={600}>
													Exceeded
												</Typography>
											) : null}
										</Box>
										<LinearProgress
											variant="determinate"
											value={Math.min(100, Math.max(0, usagePercentage))}
											color={usageColor}
											sx={{ height: 7, borderRadius: 3 }}
										/>
									</Box>

									<Tooltip title={device.is_stale ? "Data is stale" : ""} arrow>
										<Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
											<AccessTimeIcon
												fontSize="inherit"
												sx={{ color: device.is_stale ? "var(--color-warning-main)" : "text.secondary" }}
											/>
											<Typography
												variant="caption"
												color={device.is_stale ? "var(--color-warning-main)" : "text.secondary"}
											>
												Last reported: {formatDateTime(device.last_reported_at)}
											</Typography>
										</Box>
									</Tooltip>
								</Box>
							);
						})}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ImageLimitsTab;
